/**
 * @file ClientSettingCommunication.js
 * @description This file contains the ClientSettingCommunication component, which is a reusable form for displaying and editing an client's communication information.
 */

import { useState, useEffect } from 'react';
import { SearchableDropdown } from '../../helpers/SearchableDropdown';
import { Dropdown } from '../../helpers/Dropdown';
import {
    getClientOptions,
    createClientOption,
    updateClientOption,
    deleteClientOption,
    getMessages,
} from '../../../services/clientService';
import Toast from '../../ui/toasts/Toast';
import {
    SecurityAttributes,
    ClientOptionEnum,
} from '../../../utils/data/enums';
import { useAuthContext } from '../../../store/Contexts';
import ClientOptionButtons from './ClientOptionButtons';
import { getText } from '../../../i18n';

const ClientSettingCommunication = () => {
    const SYSTEM_COMMUNICATION_DEFAULT_VALUE = 'SYSTEM_COMMUNICATION_DEFAULT';

    const systemCommunicationDefaultOption = {
        label: getText('msg_e0aa7a5440e4'),
        value: SYSTEM_COMMUNICATION_DEFAULT_VALUE,
    };

    const [
        initialOptOutOfCategoriesOption,
        setInitialOptOutOfCategoriesOption,
    ] = useState({});

    const optOutOfCategoriesOptions =
        initialOptOutOfCategoriesOption?.selectList || [];

    const [selectedOptOutOfCategoryIds, setSelectedOptOutOfCategoryIds] =
        useState([]);

    const mappedOptOutOfCategoryOptions = optOutOfCategoriesOptions.map(
        opt => ({
            name: opt.label,
            id: opt.id,
        }),
    );

    const [communicationPreference, setCommunicationPreference] = useState(1);
    const [
        initialCommunicationPreferenceObject,
        setInitialCommunicationPreferenceObject,
    ] = useState();

    const [senderName, setSenderName] = useState(null);
    const [initialSenderName, setInitialSenderName] = useState(null);
    const [
        initialRegistrationCommunicationObject,
        setInitialRegistrationCommunicationObject,
    ] = useState();
    const [
        initialPasswordResetCommunicationObject,
        setInitialPasswordResetCommunicationObject,
    ] = useState();
    const [
        initialAdditionalUserEmailCommunicationObject,
        setInitialAdditionalUserEmailCommunicationObject,
    ] = useState();
    const [registrationMethod, setRegistrationMethod] = useState('');
    const [passwordResetMethod, setPasswordResetMethod] = useState('');
    const [additionalUserEmailMethod, setAdditionalUserEmailMethod] =
        useState('');
    const [automatedCommunicationOptions, setAutomatedCommunicationOptions] =
        useState([systemCommunicationDefaultOption]);

    const { hasAttribute } = useAuthContext();

    const getOptions = async () => {
        try {
            const [data, notifications] = await Promise.all([
                getClientOptions(0, [
                    ClientOptionEnum.OptOutOfCategories,
                    ClientOptionEnum.CommunicationPreference,
                    ClientOptionEnum.EmailSenderName,
                    ClientOptionEnum.RegistrationCommunication,
                    ClientOptionEnum.PasswordResetCommunication,
                    ClientOptionEnum.AdditionalUserEmailCommunication,
                ]),
                getMessages(0),
            ]);

            const activeNotificationOptions = notifications
                .filter(
                    notification =>
                        notification.notificationStatus === 'Active',
                )
                .map(notification => ({
                    label: notification.name,
                    value: String(notification.id),
                }));

            setAutomatedCommunicationOptions([
                systemCommunicationDefaultOption,
                ...activeNotificationOptions,
            ]);

            const optOutOfCategoriesObject = data.find(
                item =>
                    item.clientOptionId === ClientOptionEnum.OptOutOfCategories,
            );

            if (optOutOfCategoriesObject) {
                setInitialOptOutOfCategoriesOption(optOutOfCategoriesObject);
                setSelectedOptOutOfCategoryIds(optOutOfCategoriesObject.values);
            } else {
                Toast.error(
                    getText('msg_5c61a27bbeb2'),
                );
            }

            const communicationPreferencesClientSetting = data.find(
                item =>
                    item.clientOptionId ===
                    ClientOptionEnum.CommunicationPreference,
            );

            if (communicationPreferencesClientSetting) {
                setInitialCommunicationPreferenceObject(
                    communicationPreferencesClientSetting,
                );
                setCommunicationPreference(
                    Number(communicationPreferencesClientSetting.value),
                );
            } else {
                Toast.error(
                    getText('msg_79afac99ff33'),
                );
            }

            const emailSenderNameObject = data.find(
                item =>
                    item.clientOptionId === ClientOptionEnum.EmailSenderName,
            );

            if (emailSenderNameObject) {
                setSenderName(emailSenderNameObject.value);
                setInitialSenderName(emailSenderNameObject.value);
            } else {
                Toast.error(
                    getText('msg_4c71df48a5a8'),
                );
            }

            const registrationCommunicationSetting = data.find(
                item =>
                    item.clientOptionId ===
                    ClientOptionEnum.RegistrationCommunication,
            );
            if (registrationCommunicationSetting) {
                setInitialRegistrationCommunicationObject(
                    registrationCommunicationSetting,
                );
                setRegistrationMethod(
                    registrationCommunicationSetting.usingDefaultValue
                        ? SYSTEM_COMMUNICATION_DEFAULT_VALUE
                        : String(registrationCommunicationSetting.value || ''),
                );
            }

            const passwordResetCommunicationSetting = data.find(
                item =>
                    item.clientOptionId ===
                    ClientOptionEnum.PasswordResetCommunication,
            );
            if (passwordResetCommunicationSetting) {
                setInitialPasswordResetCommunicationObject(
                    passwordResetCommunicationSetting,
                );
                setPasswordResetMethod(
                    passwordResetCommunicationSetting.usingDefaultValue
                        ? SYSTEM_COMMUNICATION_DEFAULT_VALUE
                        : String(passwordResetCommunicationSetting.value || ''),
                );
            }

            const additionalUserEmailCommunicationSetting = data.find(
                item =>
                    item.clientOptionId ===
                    ClientOptionEnum.AdditionalUserEmailCommunication,
            );
            if (additionalUserEmailCommunicationSetting) {
                setInitialAdditionalUserEmailCommunicationObject(
                    additionalUserEmailCommunicationSetting,
                );
                setAdditionalUserEmailMethod(
                    additionalUserEmailCommunicationSetting.usingDefaultValue
                        ? SYSTEM_COMMUNICATION_DEFAULT_VALUE
                        : String(
                            additionalUserEmailCommunicationSetting.value ||
                            '',
                        ),
                );
            }
        } catch (error) {
            console.error('Could not fetch:', error);
        }
    };

    useEffect(() => {
        getOptions();
    }, []);

    const handleOptOutOfCategoriesChange = selectedOptions => {
        const ids = selectedOptions.map(opt => opt.id);
        setSelectedOptOutOfCategoryIds(ids);
    };

    const areArraysEqual = (arr1, arr2) => {
        if (arr1.length !== arr2.length) return false;
        for (let i = 0; i < arr1.length; i++) {
            if (arr1[i] !== arr2[i]) return false;
        }
        return true;
    };

    const saveAutomatedCommunicationOption = async (
        selectedValue,
        initialOption,
        clientOptionId,
        setInitialOption,
        errorLabel,
    ) => {
        if (!initialOption) {
            return false;
        }

        const isSystemDefaultSelection =
            selectedValue === SYSTEM_COMMUNICATION_DEFAULT_VALUE;
        const initialValue = String(initialOption.value || '');
        const currentValue = String(selectedValue || '');

        if (isSystemDefaultSelection && !initialOption.usingDefaultValue) {
            const deleteResponse = await deleteClientOption(0, clientOptionId);
            if (deleteResponse?.error) {
                Toast.error(
                    `There was an error attempting to reset the ${errorLabel} setting.`,
                );
                throw new Error('Delete automated communication option failed');
            }

            const [refetchedOption] = await getClientOptions(0, [
                clientOptionId,
            ]);

            if (refetchedOption) {
                setInitialOption(refetchedOption);
            }

            return true;
        }

        if (isSystemDefaultSelection && initialOption.usingDefaultValue) {
            return false;
        }

        if (!initialOption.usingDefaultValue && initialValue === currentValue) {
            return false;
        }

        if (!currentValue) {
            return false;
        }

        const optionPayload = {
            clientOptionId,
            value: currentValue,
        };

        let response;
        if (initialOption.usingDefaultValue) {
            response = await createClientOption(optionPayload, 0);
        } else {
            response = await updateClientOption(optionPayload, 0);
        }

        if (response.error) {
            Toast.error(
                `There was an error attempting to save the ${errorLabel} setting.`,
            );
            throw new Error('Save automated communication option failed');
        }

        setInitialOption(response.data);
        return true;
    };

    const handleSave = async () => {
        let noChanges = true;

        if (
            initialOptOutOfCategoriesOption.usingDefaultValue &&
            selectedOptOutOfCategoryIds.length !== 0
        ) {
            //make a post
            noChanges = false;
            const option = {
                clientOptionId: initialOptOutOfCategoriesOption.clientOptionId,
                values: selectedOptOutOfCategoryIds,
            };
            const response = await createClientOption(option, 0);
            if (response.error)
                return Toast.error(
                    getText('msg_0596e5ffec35'),
                );
            setInitialOptOutOfCategoriesOption(response.data);
        } else if (
            !areArraysEqual(
                initialOptOutOfCategoriesOption.values,
                selectedOptOutOfCategoryIds,
            )
        ) {
            //make a put/delete
            noChanges = false;

            if (selectedOptOutOfCategoryIds.length === 0) {
                const deleteResponse = await deleteClientOption(
                    0,
                    initialOptOutOfCategoriesOption.clientOptionId,
                );

                if (deleteResponse.error)
                    return Toast.error(
                        getText('msg_1da32872998e'),
                    );

                const data = await getClientOptions(0, [
                    ClientOptionEnum.OptOutOfCategories,
                ]);

                const optOutOfCategoriesObject = data.find(
                    item => item.name === 'Opt Out Of Categories',
                );
                if (optOutOfCategoriesObject) {
                    setInitialOptOutOfCategoriesOption(
                        optOutOfCategoriesObject,
                    );
                }
            } else {
                const option = {
                    clientOptionId:
                        initialOptOutOfCategoriesOption.clientOptionId,
                    values: selectedOptOutOfCategoryIds,
                };
                const response = await updateClientOption(option, 0);
                if (response.error)
                    return Toast.error(
                        getText('msg_1da32872998e'),
                    );
                setInitialOptOutOfCategoriesOption(response.data);
            }
        }

        // communication preferences
        if (
            Number(initialCommunicationPreferenceObject.value) !==
            communicationPreference
        ) {
            noChanges = false;

            const option = {
                clientOptionId: ClientOptionEnum.CommunicationPreference,
                value: communicationPreference.toString(),
            };

            var response;

            if (initialCommunicationPreferenceObject.usingDefaultValue) {
                response = await createClientOption(option, 0);
                if (response.error)
                    return Toast.error(
                        getText('msg_0ca87aa4388f'),
                    );
            } else {
                response = await updateClientOption(option, 0);
                if (response.error)
                    return Toast.error(
                        getText('msg_3d5cce66a00f'),
                    );
            }

            setInitialCommunicationPreferenceObject(response.data);
        }

        // email sender name
        if (senderName !== initialSenderName) {
            noChanges = false;

            const option = {
                clientOptionId: ClientOptionEnum.EmailSenderName,
                value: senderName,
            };

            let emailSenderNameResponse;

            if (initialSenderName === null) {
                emailSenderNameResponse = await createClientOption(option, 0);
                if (emailSenderNameResponse.error)
                    return Toast.error(
                        getText('msg_ae99f7585e33'),
                    );
            } else {
                emailSenderNameResponse = await updateClientOption(option, 0);
                if (emailSenderNameResponse.error)
                    return Toast.error(
                        getText('msg_d483c0b39925'),
                    );
            }

            setInitialSenderName(emailSenderNameResponse.data.value);
        }

        try {
            const registrationChanged = await saveAutomatedCommunicationOption(
                registrationMethod,
                initialRegistrationCommunicationObject,
                ClientOptionEnum.RegistrationCommunication,
                setInitialRegistrationCommunicationObject,
                'registration communication',
            );

            const passwordResetChanged = await saveAutomatedCommunicationOption(
                passwordResetMethod,
                initialPasswordResetCommunicationObject,
                ClientOptionEnum.PasswordResetCommunication,
                setInitialPasswordResetCommunicationObject,
                'password reset communication',
            );

            const additionalUserEmailChanged =
                await saveAutomatedCommunicationOption(
                    additionalUserEmailMethod,
                    initialAdditionalUserEmailCommunicationObject,
                    ClientOptionEnum.AdditionalUserEmailCommunication,
                    setInitialAdditionalUserEmailCommunicationObject,
                    'additional user email communication',
                );

            if (
                registrationChanged ||
                passwordResetChanged ||
                additionalUserEmailChanged
            ) {
                noChanges = false;
            }
        } catch (error) {
            return;
        }

        if (noChanges) Toast.warning(getText('msg_13c3a7ac4e47'));
        else Toast.success(getText('msg_ee0ac475c672'));
    };

    const handleCancel = () => {
        setSelectedOptOutOfCategoryIds(initialOptOutOfCategoriesOption.values);
        setCommunicationPreference(
            Number(initialCommunicationPreferenceObject.value),
        );
        setSenderName(initialSenderName);
        setRegistrationMethod(
            initialRegistrationCommunicationObject?.usingDefaultValue
                ? SYSTEM_COMMUNICATION_DEFAULT_VALUE
                : String(initialRegistrationCommunicationObject?.value || ''),
        );
        setPasswordResetMethod(
            initialPasswordResetCommunicationObject?.usingDefaultValue
                ? SYSTEM_COMMUNICATION_DEFAULT_VALUE
                : String(initialPasswordResetCommunicationObject?.value || ''),
        );
        setAdditionalUserEmailMethod(
            initialAdditionalUserEmailCommunicationObject?.usingDefaultValue
                ? SYSTEM_COMMUNICATION_DEFAULT_VALUE
                : String(
                    initialAdditionalUserEmailCommunicationObject?.value ||
                    '',
                ),
        );
    };

    return (
        <>
            <div
                style={{
                    marginLeft: '48px',
                    marginRight: '48px',
                    marginTop: '12px',
                }}
            >
                <div>{getText('msg_38dea7718166')}</div>
                <div
                    style={{
                        color: 'var(--iNGAGED-Neutral-Color2)',
                        marginBottom: '12px',
                    }}
                >
                    {getText('msg_784c23ede86b')}
                </div>
                <div>
                    <SearchableDropdown
                        placeholderLabel={getText('msg_8598222918d3')}
                        options={mappedOptOutOfCategoryOptions}
                        optionIDField="id"
                        savedOptions={mappedOptOutOfCategoryOptions.filter(
                            opt => selectedOptOutOfCategoryIds.includes(opt.id),
                        )}
                        savedOptionIDField="id"
                        updateOptions={selectedOptions =>
                            handleOptOutOfCategoriesChange(selectedOptions)
                        }
                        disabled={false}
                        showTags={true}
                    />
                </div>
            </div>

            <div
                style={{
                    marginLeft: '48px',
                    marginRight: '48px',
                    marginTop: '24px',
                }}
            >
                <div>{getText('msg_88d7dacc35f7')}</div>
                <div
                    style={{
                        color: 'var(--iNGAGED-Neutral-Color2)',
                        marginBottom: '12px',
                    }}
                >
                    {getText('msg_df69cc72280d')}
                </div>

                <input
                    className="form-check-input"
                    type="radio"
                    name="radioEmail"
                    id="radioEmail"
                    value={1}
                    checked={communicationPreference === 1}
                    onChange={() => setCommunicationPreference(1)}
                />
                <label className="form-check-label" htmlFor="radioEmail">
                    {getText('msg_84add5b29527')}
                </label>

                <input
                    className="form-check-input"
                    type="radio"
                    name="radioText"
                    id="radioText"
                    value={2}
                    checked={communicationPreference === 2}
                    onChange={() => setCommunicationPreference(2)}
                    style={{ marginLeft: '30px' }}
                />
                <label className="form-check-label" htmlFor="radioText">
                    {getText('msg_c3328c39b0e2')}
                </label>
            </div>

            <div
                style={{
                    marginLeft: '48px',
                    marginRight: '48px',
                    marginTop: '24px',
                }}
            >
                <div>{getText('msg_bc189e601c36')}</div>
                <div
                    style={{
                        color: 'var(--iNGAGED-Neutral-Color2)',
                        marginBottom: '12px',
                    }}
                >
                    {getText('msg_0432abad0ba6')}
                </div>
                <input
                    type="text"
                    id="sender_name"
                    name="sender_name"
                    value={senderName || ''}
                    onChange={event => setSenderName(event.target.value)}
                    className={`form-control`}
                    maxLength={256}
                />
            </div>

            <div
                style={{
                    marginLeft: '48px',
                    marginRight: '48px',
                    marginTop: '24px',
                }}
            >
                <div>{getText('msg_fd66a8fe0fee')}</div>
                <div
                    style={{
                        color: 'var(--iNGAGED-Neutral-Color2)',
                        marginBottom: '12px',
                    }}
                >
                    {getText('msg_22996f2bdee6')}
                </div>

                <div style={{ marginBottom: '16px' }}>
                    <div style={{ marginBottom: '6px' }}>{getText('msg_b233e775d63b')}</div>
                    <Dropdown
                        defaultValue={
                            automatedCommunicationOptions.find(
                                opt => opt.value === registrationMethod,
                            )?.label || systemCommunicationDefaultOption.label
                        }
                        options={automatedCommunicationOptions}
                        onChange={setRegistrationMethod}
                    />
                </div>

                <div style={{ marginBottom: '16px' }}>
                    <div style={{ marginBottom: '6px' }}>{getText('msg_79167df1dd0b')}</div>
                    <Dropdown
                        defaultValue={
                            automatedCommunicationOptions.find(
                                opt => opt.value === passwordResetMethod,
                            )?.label || systemCommunicationDefaultOption.label
                        }
                        options={automatedCommunicationOptions}
                        onChange={setPasswordResetMethod}
                    />
                </div>

                <div>
                    <div style={{ marginBottom: '6px' }}>
                        {getText('msg_c3c7b3b32ea7')}
                    </div>
                    <Dropdown
                        defaultValue={
                            automatedCommunicationOptions.find(
                                opt => opt.value === additionalUserEmailMethod,
                            )?.label || systemCommunicationDefaultOption.label
                        }
                        options={automatedCommunicationOptions}
                        onChange={setAdditionalUserEmailMethod}
                    />
                </div>
            </div>

            {hasAttribute(
                SecurityAttributes.ClientSettingsFullAccess
            ) && (
                    <ClientOptionButtons
                        handleSave={handleSave}
                        handleCancel={handleCancel}
                    />
                )}
        </>
    );
};

export default ClientSettingCommunication;
